import { Quote, Star } from "lucide-react";
import { SectionEyebrow } from "./section-eyebrow";

export type Testimonial = {
  quote: string;
  name: string;
  role: string;
  company: string;
  initials: string;
  hue?: number;
  metric?: { value: string; label: string };
  metrics?: { value: string; label: string }[];
  rating?: number;
  featured?: boolean;
};

export function TestimonialsMasonry({
  items,
  eyebrow = "Loved by lean teams",
  heading = "Founders shipping growth on autopilot.",
}: {
  items: Testimonial[];
  eyebrow?: string;
  heading?: string;
}) {
  return (
    <section className="px-6 py-24">
      <div className="max-w-7xl mx-auto">
        <SectionEyebrow>{eyebrow}</SectionEyebrow>
        <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-white text-balance mb-12 max-w-3xl">
          {highlightAutopilot(heading)}
        </h2>

        {/* CSS columns masonry — varied card heights, no JS */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
          {items.map((t, i) => (
            <Card key={t.name + i} t={t} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

function Card({ t, index }: { t: Testimonial; index: number }) {
  const hue = t.hue ?? 290 + index * 12;
  const featured = t.featured;
  const rating = t.rating ?? 5;

  return (
    <article
      className={`mb-5 break-inside-avoid glass-card relative overflow-hidden p-6 transition-all duration-500 hover:-translate-y-1 hover:border-white/25 ${
        featured ? "p-7" : ""
      }`}
    >
      {/* Glow */}
      <div
        aria-hidden
        className="absolute -top-16 -right-16 size-48 rounded-full blur-3xl pointer-events-none"
        style={{
          background: `radial-gradient(circle, hsla(${hue}, 80%, 60%, ${
            featured ? 0.5 : 0.28
          }), transparent 65%)`,
        }}
      />

      {featured && (
        <div className="relative mb-4 flex items-center gap-2">
          <span
            className="grid place-items-center size-8 rounded-xl"
            style={{
              background: "linear-gradient(135deg, rgba(244,114,208,0.6), rgba(123,63,242,0.6))",
              boxShadow: "0 10px 30px rgba(217,70,239,0.45)",
            }}
          >
            <Quote className="size-4 text-white" />
          </span>
          <span className="text-[10px] uppercase tracking-[0.22em] text-pink-soft font-medium">
            Featured story
          </span>
        </div>
      )}

      <div className="relative flex items-center gap-1 mb-3">
        {Array.from({ length: 5 }).map((_, i) => (
          <Star
            key={i}
            className={`size-3.5 ${i < rating ? "fill-pink-soft text-pink-soft" : "text-white/20"}`}
          />
        ))}
      </div>

      <figure className="relative m-0">
        <blockquote
          className={`text-white/90 leading-relaxed text-pretty ${
            featured ? "text-lg" : "text-[15px]"
          }`}
        >
          {t.quote}
        </blockquote>

        {(t.metrics ?? (t.metric ? [t.metric] : [])).length > 0 && (
          <div className="mt-5 flex flex-wrap gap-2">
            {(t.metrics ?? (t.metric ? [t.metric] : [])).map((m, idx) => (
              <div
                key={idx}
                className="inline-flex items-baseline gap-2 rounded-full glass-pill px-3 py-1.5"
              >
                <span className="text-sm font-semibold text-gradient tabular-nums">{m.value}</span>
                <span className="text-[11px] uppercase tracking-wider text-white/60">
                  {m.label}
                </span>
              </div>
            ))}
          </div>
        )}

        <figcaption className="mt-6 flex items-center gap-3">
          <span
            aria-hidden
            className="grid place-items-center size-10 rounded-full text-sm font-semibold text-white border border-white/20"
            style={{
              background: `linear-gradient(135deg, hsla(${hue}, 75%, 55%, 0.95), hsla(${
                hue + 30
              }, 80%, 45%, 0.95))`,
            }}
          >
            {t.initials}
          </span>
          <cite className="min-w-0 not-italic">
            <span className="block text-sm font-semibold text-white truncate">{t.name}</span>
            <span className="block text-xs text-white/55 truncate">
              {t.role} · {t.company}
            </span>
          </cite>
        </figcaption>
      </figure>
    </article>
  );
}

function highlightAutopilot(text: string) {
  const parts = text.split(/(autopilot)/gi);
  return parts.map((part, i) =>
    part.toLowerCase() === "autopilot" ? (
      <span key={i} className="text-gradient">
        {part}
      </span>
    ) : (
      part
    )
  );
}
